import React, { useState, useEffect } from "react";
import { useQuery, useMutation } from "@tanstack/react-query";
import { useLocation, useParams, useRoute } from "wouter";
import { apiRequest, queryClient } from "@/lib/queryClient";
import { useToast } from "@/hooks/use-toast";
import { useAuth } from "@/hooks/use-auth";
import { Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Label } from "@/components/ui/label";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Textarea } from "@/components/ui/textarea";
import { CharacterVoiceSelector } from "@/components/character-voice-selector";
import StripePayment from "@/components/stripe-payment";
import { getStyleById, renderStyles } from "@/lib/render-styles";
import { ScriptLine, BlockType, CharacterProfile } from "@shared/schema";

// Define the screenplay data structure
interface EnhancedScreenplay {
  id: string;
  userId: string;
  title: string;
  originalFileName: string;
  filePath: string;
  status: string;
  style: string;
  notes?: string;
  blocks: ScriptLine[];
  characterDictionary: Record<string, CharacterProfile>;
  sceneDictionary: Record<string, string>;
  pageCount: number;
  estimatedCost: number;
  estimatedRenderTime: string;
  uploadedAt: string;
  characterVoices?: Record<string, any>; // For auto-assigned voices
}

// Component for the pre-render configuration page
export default function PrepRenderPage() {
  const { id } = useParams<{ id: string }>();
  const screenplayId = id || "";
  const [, navigate] = useLocation();
  const { toast } = useToast();
  const { user } = useAuth();
  
  // State for form values
  const [renderTitle, setRenderTitle] = useState("");
  const [renderStyle, setRenderStyle] = useState("cinematic");
  // Remove demo render type - only full renders now
  const [notes, setNotes] = useState("");
  const [characterVoices, setCharacterVoices] = useState<Record<string, {id: string, name: string}>>({});
  const [characters, setCharacters] = useState<Array<{ 
    name: string, 
    selectedVoice: string,
    pronouns?: {
      subject: string;    // e.g., he/she/they
      object: string;     // e.g., him/her/them
      possessive: string; // e.g., his/her/their
    };
  }>>([]);
  const [audioPlayer, setAudioPlayer] = useState<HTMLAudioElement | null>(null);
  const [voices, setVoices] = useState<Record<string, Array<{id: string, name: string, gender: string, preview_url?: string}>>>({male: [], female: [], neutral: []});
  
  // Load available voices
  useEffect(() => {
    const fetchVoices = async () => {
      try {
        const response = await apiRequest("GET", "/api/voices");
        const data = await response.json();
        
        const maleVoices = data.filter((v: any) => v.labels?.gender === 'male');
        const femaleVoices = data.filter((v: any) => v.labels?.gender === 'female');
        const neutralVoices = data.filter((v: any) => 
          !v.labels?.gender || v.labels?.gender === 'non-binary'
        );
        
        setVoices({
          male: maleVoices.map((v: any) => ({
            id: v.voice_id,
            name: v.name,
            gender: 'male',
            preview_url: v.preview_url
          })),
          female: femaleVoices.map((v: any) => ({
            id: v.voice_id,
            name: v.name,
            gender: 'female',
            preview_url: v.preview_url
          })),
          neutral: neutralVoices.map((v: any) => ({
            id: v.voice_id,
            name: v.name,
            gender: 'neutral',
            preview_url: v.preview_url
          }))
        });
      } catch (error) {
        console.error("Failed to load voices:", error);
      }
    };
    
    fetchVoices();
  }, []);
  
  // Query for screenplay data
  const { data: screenplay, isLoading: isLoadingScreenplay, error: screenplayError } = useQuery<EnhancedScreenplay>({
    queryKey: [`/api/screenplays/${screenplayId}`],
    enabled: !!screenplayId
  });

  // Query for user points and subscription
  const { data: userPoints = 0 } = useQuery<number>({
    queryKey: ["/api/user/points"],
    enabled: !!user
  });

  const { data: subscription } = useQuery({
    queryKey: ["/api/user/subscription"],
    enabled: !!user
  });
  
  // Set initial data from screenplay
  useEffect(() => {
    if (screenplay) {
      // Handle screenplay data safely with optional chaining
      const title = screenplay?.title || `Screenplay ${screenplay?.id || screenplayId}`;
      setRenderTitle(`${title} Render`);
      
      // Extract characters from character dictionary if available
      const characterDict = screenplay?.characterDictionary || {};
      const characterVoicesFromAPI = screenplay?.characterVoices || {};
      
      if (Object.keys(characterDict).length > 0) {
        // Create array of characters with any pre-assigned voices and pronouns
        const extractedCharacters = Object.keys(characterDict).map(name => {
          const characterInfo = characterDict[name];
          const assignedVoiceData = characterVoicesFromAPI[name];
          let voiceId = assignedVoiceData?.voice_id || "";
          let voiceName = assignedVoiceData?.name || "";
          
          // Special handling for NARRATOR - always override with George voice
          if (name === 'NARRATOR') {
            voiceId = "Yko7PKHZNXotIFUBG7I9";
            voiceName = "George";
          }
          
          // If we have a voice assignment, add it to the characterVoices state
          if (voiceId) {
            setCharacterVoices(prev => ({
              ...prev,
              [name]: {
                id: voiceId,
                name: voiceName
              }
            }));
          }
          
          return {
            name,
            selectedVoice: voiceId,
            pronouns: characterInfo?.pronouns || {
              subject: 'they',
              object: 'them',
              possessive: 'their'
            }
          };
        });
        
        console.log('Extracted characters with pronouns:', extractedCharacters);
        setCharacters(extractedCharacters);
      } else {
        // If no character dictionary is available, create a sample for testing
        setCharacters([
          { 
            name: "John", 
            selectedVoice: "",
            pronouns: { subject: 'he', object: 'him', possessive: 'his' }
          },
          { 
            name: "Mary", 
            selectedVoice: "",
            pronouns: { subject: 'she', object: 'her', possessive: 'her' }
          }
        ]);
      }
    }
  }, [screenplay, screenplayId]);
  
  // State for payment flow
  const [showPayment, setShowPayment] = useState(false);
  
  // Mutation for creating render job with points
  const createRenderMutation = useMutation({
    mutationFn: async (data: {
      screenplayId: string,
      title: string,
      style: string,
      characterVoices: Record<string, {id: string, name: string}>
    }) => {
      const response = await apiRequest("POST", "/api/renders", data);
      return await response.json();
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ["/api/renders"] });
      queryClient.invalidateQueries({ queryKey: ["/api/user/points"] });
      
      toast({
        title: "Render job created!",
        description: "Your render has been queued for processing.",
      });
      
      // Navigate to render details
      navigate(`/renders/${data.id}`);
    },
    onError: (error: Error) => {
      toast({
        title: "Error creating render job",
        description: error.message,
        variant: "destructive"
      });
    }
  });
  
  // Mutation for creating render job after payment (for additional points)
  const createPaidRenderMutation = useMutation({
    mutationFn: async (data: { paymentIntentId: string, screenplayId: string, style: string, renderTitle: string, characterVoices: Record<string, {id: string, name: string}> }) => {
      const response = await apiRequest("POST", "/api/purchase-render", data);
      return await response.json();
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ["/api/renders"] });
      queryClient.invalidateQueries({ queryKey: ["/api/user/points"] });
      
      toast({
        title: "Render job created!",
        description: "Your payment was successful and your render has been queued for processing.",
      });
      
      // Navigate to render details
      navigate(`/renders/${data.renderJob.id}`);
    },
    onError: (error: Error) => {
      toast({
        title: "Error creating render job",
        description: error.message,
        variant: "destructive"
      });
    }
  });
  
  // Calculate render cost in points (100 points = 1 page)
  const calculateRenderCost = () => {
    const pageCount = screenplay?.pageCount || 1;
    return pageCount * 100; // 100 points per page
  };

  // Calculate cost in USD (points / 100 * $2.00)
  const calculateUSDCost = (points: number) => {
    return (points / 100) * 2.00;
  };

  // Handle form submission
  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    
    const renderCost = calculateRenderCost();
    
    // Check if user has enough points (regardless of subscription status)
    if (userPoints >= renderCost) {
      // User has enough points - render directly using points
      createRenderMutation.mutate({
        screenplayId,
        title: renderTitle,
        style: renderStyle,
        characterVoices
      });
    } else {
      // Need to show payment for additional points or full cost
      setShowPayment(true);
    }
  };
  
  // Handle payment success
  const handlePaymentSuccess = (paymentIntentId: string) => {
    // Create the render job with the payment intent
    createPaidRenderMutation.mutate({
      paymentIntentId,
      screenplayId,
      style: renderStyle,
      renderTitle: renderTitle,
      characterVoices
    });
  };
  
  // Handle payment cancellation
  const handlePaymentCancel = () => {
    setShowPayment(false);
  };
  
  // Handle voice change
  const handleVoiceChange = (characterName: string, voiceId: string) => {
    // Find the voice details across all voice categories
    const allAvailableVoices = [
      ...(voices.male || []),
      ...(voices.female || []),
      ...(voices.neutral || [])
    ];
    const selectedVoice = allAvailableVoices.find(voice => voice.id === voiceId);
    
    if (selectedVoice) {
      console.log("Selected voice:", selectedVoice);
      
      // Update character voice in state with both ID and name
      setCharacterVoices(prev => ({
        ...prev,
        [characterName]: {
          id: voiceId,
          name: selectedVoice.name
        }
      }));
    } else {
      console.error(`Voice with ID ${voiceId} not found`);
      return;
    }
    
    // Update the characters array for the UI
    setCharacters(prev => 
      prev.map(char => 
        char.name === characterName 
          ? { ...char, selectedVoice: voiceId } 
          : char
      )
    );
  };
  
  // Helper function to get voices for a character - now returns all voices to allow full choice
  const getVoicesForCharacter = (characterName: string) => {
    // Return all available voices so users can choose any voice for any character
    return [...(voices.male || []), ...(voices.female || []), ...(voices.neutral || [])];
  };
  
  // Handle voice preview
  const handlePreviewVoice = (previewUrl: string) => {
    if (audioPlayer) {
      audioPlayer.pause();
      audioPlayer.src = previewUrl;
      audioPlayer.play();
    } else {
      const player = new Audio(previewUrl);
      player.play();
      setAudioPlayer(player);
    }
  };
  
  // Show loading state
  if (isLoadingScreenplay) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
        <span className="ml-2">Loading screenplay data...</span>
      </div>
    );
  }
  
  // Show error state
  if (screenplayError || !screenplay) {
    return (
      <div className="container max-w-4xl mx-auto py-8">
        <Card>
          <CardHeader>
            <CardTitle>Error</CardTitle>
            <CardDescription>
              Could not load screenplay data. Please try again or go back to your screenplays.
            </CardDescription>
          </CardHeader>
          <CardContent>
            <Button onClick={() => navigate("/screenplays")}>Back to Screenplays</Button>
          </CardContent>
        </Card>
      </div>
    );
  }
  
  // If showing payment UI, render the payment component
  if (showPayment && screenplay) {
    const renderCost = calculateRenderCost();
    const pointsToCharge = subscription && userPoints >= renderCost 
      ? 0 
      : subscription 
        ? (renderCost - userPoints) 
        : renderCost;
    const amountInCents = Math.round(calculateUSDCost(pointsToCharge) * 100);
    
    return (
      <div className="container max-w-4xl mx-auto py-8">
        <div className="mb-8">
          <h1 className="text-3xl font-bold">Complete Your Purchase</h1>
          <p className="text-muted-foreground">
            {subscription 
              ? `Payment for ${pointsToCharge} additional points` 
              : `Payment for ${pointsToCharge} points to render "${screenplay?.title || 'Your Screenplay'}"`}
          </p>
        </div>
        
        <StripePayment
          screenplayId={screenplayId}
          amount={amountInCents}
          title={screenplay.title}
          onPaymentSuccess={handlePaymentSuccess}
          onCancel={handlePaymentCancel}
        />
      </div>
    );
  }

  // Main render configuration UI
  return (
    <div className="container max-w-4xl mx-auto py-8">
      <div className="flex justify-between items-start mb-8">
        <div>
          <h1 className="text-3xl font-bold">Prepare Render</h1>
          <p className="text-muted-foreground">
            Configure your render options for "{screenplay?.title || 'Your Screenplay'}"
          </p>
        </div>
        <Button 
          variant="outline" 
          onClick={() => navigate(`/screenplays/${screenplayId}`)}
          className="flex items-center"
        >
          <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="mr-2">
            <path d="m15 18-6-6 6-6"/>
          </svg>
          Back to Screenplay Page
        </Button>
      </div>
      
      <form onSubmit={handleSubmit}>
        <div className="space-y-6">
          {/* Basic render information */}
          <Card>
            <CardHeader>
              <CardTitle>Render Details</CardTitle>
              <CardDescription>
                Provide a title and choose your render type
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div>
                <Label htmlFor="title">Render Title</Label>
                <input
                  id="title"
                  className="w-full mt-1 px-3 py-2 border border-input rounded-md"
                  value={renderTitle}
                  onChange={(e) => setRenderTitle(e.target.value)}
                  required
                />
              </div>
              
              {/* Render Cost Display */}
              <div className="bg-gradient-to-r from-blue-50 to-purple-50 border border-blue-200 rounded-lg p-4">
                <div className="flex items-center justify-between mb-2">
                  <h3 className="font-semibold text-lg">Render Cost</h3>
                  <div className="text-right">
                    <div className="text-2xl font-bold text-blue-600">
                      {calculateRenderCost()} points
                    </div>
                    <div className="text-sm text-gray-600">
                      (${calculateUSDCost(calculateRenderCost()).toFixed(2)})
                    </div>
                  </div>
                </div>
                
                <div className="text-sm text-gray-600 mb-3">
                  Renders the entire screenplay ({screenplay?.pageCount || 1} pages)
                </div>

                {/* Payment Logic Display */}
                {userPoints >= calculateRenderCost() ? (
                  <div className="bg-green-100 border border-green-300 rounded-md p-3">
                    <div className="text-green-800 font-medium">
                      ✓ Confirm Render – 0 additional cost
                    </div>
                    <div className="text-green-600 text-sm">
                      Uses {calculateRenderCost()} of your {userPoints} available points
                    </div>
                  </div>
                ) : subscription ? (
                  <div className="bg-yellow-100 border border-yellow-300 rounded-md p-3">
                    <div className="text-yellow-800 font-medium">
                      You have {userPoints} points remaining from your subscription.
                    </div>
                    <div className="text-yellow-700 text-sm">
                      We'll charge for the extra {calculateRenderCost() - userPoints} points 
                      (${calculateUSDCost(calculateRenderCost() - userPoints).toFixed(2)}).
                    </div>
                  </div>
                ) : (
                  <div className="bg-blue-100 border border-blue-300 rounded-md p-3">
                    <div className="text-blue-800 font-medium">
                      This render costs {calculateRenderCost()} points (${calculateUSDCost(calculateRenderCost()).toFixed(2)}).
                    </div>
                    <div className="text-blue-700 text-sm">
                      {userPoints > 0 
                        ? `You have ${userPoints} points. We'll charge for the remaining ${calculateRenderCost() - userPoints} points.`
                        : "You don't have a subscription, so points will be purchased automatically."
                      }
                    </div>
                    <div className="mt-2">
                      <a href="/pricing" className="text-blue-600 hover:text-blue-800 underline text-sm">
                        Subscribe to save 20% on renders →
                      </a>
                    </div>
                  </div>
                )}
              </div>
            </CardContent>
          </Card>
          
          {/* Visual style selection */}
          <Card>
            <CardHeader>
              <CardTitle>Visual Style</CardTitle>
              <CardDescription>
                Choose the visual style for your render
              </CardDescription>
            </CardHeader>
            <CardContent>
              <RadioGroup 
                value={renderStyle} 
                onValueChange={setRenderStyle}
                className="grid grid-cols-1 md:grid-cols-2 gap-4"
              >
                {renderStyles.map((style) => (
                  <div key={style.id} className="flex items-start space-x-2 p-2 rounded-md border border-input hover:border-primary transition-colors">
                    <RadioGroupItem value={style.id} id={style.id} className="mt-1" />
                    <div>
                      <Label htmlFor={style.id} className="font-medium">{style.name}</Label>
                      <p className="text-sm text-muted-foreground">{style.description}</p>
                    </div>
                  </div>
                ))}
              </RadioGroup>
            </CardContent>
          </Card>
          
          {/* Character Voice Selection */}
          <CharacterVoiceSelector 
            characters={characters}
            onVoiceChange={handleVoiceChange}
            onPreviewVoice={handlePreviewVoice}
          />
          
          {/* Submission */}
          <div className="flex justify-end space-x-4">
            <Button variant="outline" type="button" onClick={() => navigate(`/screenplays/${screenplayId}`)}>
              Cancel
            </Button>
            
            <Button 
              type="submit" 
              disabled={createRenderMutation.isPending || createPaidRenderMutation.isPending}
              className="min-w-[150px]"
            >
              {createRenderMutation.isPending || createPaidRenderMutation.isPending ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Processing...
                </>
              ) : (
                userPoints >= calculateRenderCost() 
                  ? `Confirm Render: ${calculateRenderCost()} Points` 
                  : subscription 
                    ? `Confirm and Pay $${calculateUSDCost(calculateRenderCost() - userPoints).toFixed(2)}`
                    : `Confirm and Pay $${calculateUSDCost(calculateRenderCost()).toFixed(2)}`
              )}
            </Button>
          </div>
        </div>
      </form>
    </div>
  );
}